import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * One file attached to an outgoing email.
 *
 * `content` is base64 WITHOUT a data: prefix — the backend feeds it straight
 * into a MIME part. Attachments make hc-email switch from SES SendEmail to
 * SendRawEmail; the ceiling is 10MB for the entire message after base64
 * inflation (~1.37x), shared across all attachments.
 */
export interface EmailAttachment {
  filename: string;
  content: string;      // base64
  contentType?: string; // defaults to application/octet-stream server-side
}

export interface SendEmailRequest {
  to: string | string[];
  subject: string;
  htmlBody?: string;
  textBody?: string;
  cc?: string | string[];
  bcc?: string | string[];
  from?: string;
  replyTo?: string | string[];
  attachments?: EmailAttachment[];
  /**
   * Automatic-email category (EMAIL_CATEGORIES key). hc-email drops recipients
   * who opted out of it in Manage Users. Omit for emails a person sends on
   * purpose (e.g. Send Report) — uncategorized sends are never filtered.
   */
  category?: string;
}

export interface SendEmailResponse {
  message: string;
  messageId: string;
  optedOut?: string[];     // Recipients hc-email skipped because they opted out
  allOptedOut?: boolean;   // Everyone opted out; nothing was sent (still a 200)
}

@Injectable({
  providedIn: 'root'
})
export class EmailService {
  private http = inject(HttpClient);

  // URLs for the API endpoints
  private urlAssessmentSendEmail = environment.domainForCommunications + 'assessments/send-email-reminder';
  private urlAssessmentStatusChange = environment.domainForCommunications + 'assessments/status-change';
  private urlSendEmail = environment.domainForCommunications + 'email/send-email';
  private apiKey = environment.communicationsKeyForProd;

  constructor() { }

  /**
   * Placeholder function for getting email content from a template.
   * This is a temporary solution to resolve a compilation error.
   * @param templateName The name of the template.
   * @returns An observable of a simple placeholder string.
   */
  getEmailTemplate(templateName: string): Observable<string[]> {
    console.warn(`[Placeholder] getEmailTemplate called for template: ${templateName}. This function needs to be implemented properly.`);
    return of(['<p>This is a temporary placeholder for an email template.</p>']);
  }

  /**
   * Sends a basic email.
   * @param sendTo The recipient's email address. Can be a string or an object with an 'email' property.
   * @param subject The subject line of the email.
   * @param emailHTMLBody The HTML body of the email.
   * @returns An Observable of the API response.
   */
  sendEmail(sendTo: string | { email: string }, subject: string, emailHTMLBody: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey
    });

    const sendToEmailAddress = typeof sendTo === 'object' ? sendTo.email : sendTo;

    const jsonReadyBody = {
      sendToEmailAddress,
      subject,
      htmlBody: emailHTMLBody
    };

    return this.http.post(this.urlSendEmail, jsonReadyBody, { headers }).pipe(
      catchError(error => {
        console.error('An error occurred while sending email:', error);
        return throwError(() => new Error('Could not send email.'));
      })
    );
  }

  /**
   * Sends an email with an attachment.
   * @param emailAddress The recipient's email address.
   * @param subject The subject line of the email.
   * @param htmlBody The HTML body of the email.
   * @param attachment The email attachment object.
   * @returns An Observable of the API response.
   */
  sendEmailWithAttachment(emailAddress: string, subject: string, htmlBody: string, attachment: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const jsonReadyBody = {
      sendToEmailAddress: emailAddress,
      subject,
      htmlBody,
      emailAttachment: attachment
    };

    return this.http.put(environment.URL_PROCESSING, jsonReadyBody, { headers }).pipe(
      catchError(error => {
        console.error('An error occurred while sending email with attachment:', error);
        return throwError(() => new Error('Could not send email with attachment.'));
      })
    );
  }

  /**
   * Sends an email notification for an assessment status change.
   * @param sendTo The recipient's email address.
   * @param assessmentName The name of the assessment.
   * @param emailBody The body of the email.
   * @returns An Observable of the API response.
   */
  assessmentStatusNotificationChangeEmail(sendTo: string, assessmentName: string, emailBody: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey
    });
    const jsonReadyBody = {
      sendToEmailAddress: sendTo,
      assessmentName,
      emailBody
    };

    return this.http.post(this.urlAssessmentStatusChange, jsonReadyBody, { headers }).pipe(
      catchError(error => {
        console.error('An error occurred while sending status change email:', error);
        return throwError(() => new Error('Could not send assessment status change email.'));
      })
    );
  }

  /**
   * Sends an assessment email reminder.
   * @param sendTo The recipient's email address.
   * @param assessment The assessment object.
   * @param contract The contract object.
   * @param htmlEnding The ending HTML content for the email.
   * @returns An Observable of the API response.
   */
  assessmentSendEmailReminder(sendTo: string, assessment: any, contract: any, htmlEnding: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey
    });
    const jsonReadyBody = {
      sendToEmailAddress: sendTo,
      assessment,
      contract,
      htmlEnding
    };

    return this.http.post(this.urlAssessmentSendEmail, jsonReadyBody, { headers }).pipe(
      catchError(error => {
        console.error('An error occurred while sending assessment reminder email:', error);
        return throwError(() => new Error('Could not send assessment reminder email.'));
      })
    );
  }

  /**
   * Send an email via the new hc-all-orgs-email SES service.
   * POST /api/{org}/email/send
   */
  send(payload: SendEmailRequest): Observable<SendEmailResponse> {
    return this.http.post<SendEmailResponse>((environment as any).URL_EMAIL, payload).pipe(
      catchError(error => {
        console.error('[EmailService] Failed to send email:', error);
        return throwError(() => error);
      })
    );
  }
}
